/**
 * Cliente HTTP para o backend IP WatchDog.
 *
 * Endpoints usados:
 *   GET  /health                                            (sem auth)
 *   GET  /api/admin/devices                                 (X-Admin-Token)
 *   GET  /api/admin/devices/{device_id}                     (X-Admin-Token)
 *   POST /api/admin/devices/{device_id}/commands            (X-Admin-Token)
 *   GET  /api/admin/devices/{device_id}/commands/{cmd_id}   (X-Admin-Token)
 *
 * Sprint 2 Etapa 3d: adicionou suporte a POST + endpoints de commands.
 */

import { loadConfig } from "./config.js";
import { resolveApiBase } from "./apiBase.js";
import { clearSession, isRecoveryActive, loadSession, saveSession } from "./auth.js";

class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status ?? null;
    this.body = body ?? null;
  }
}

function buildUrl(path) {
  const { apiUrl } = loadConfig();
  // INSTALLER-LOGIN-NETWORK-DIAG-1 — evita "mixed content" (página https + base
  // http:// = fetch bloqueado → "Failed to fetch"). Cai p/ same-origin (Caddy
  // roteia /api/*). Em http/dev ou base https explícita, respeita a base.
  const proto =
    (typeof window !== "undefined" && window.location && window.location.protocol) || "";
  const base = resolveApiBase(apiUrl, proto).replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

// SAAS-AUTH-RETIRE-ADMIN-TOKEN — handler global de sessão expirada/inválida.
// A App registra um callback; em 401 numa chamada autenticada com sessão Bearer,
// limpamos a sessão e disparamos o callback (redirect à tela de login) em vez de
// cair silenciosamente no fallback X-Admin-Token. Bench (ADMIN_TOKEN, sem sessão)
// NÃO é deslogado aqui — preserva a credencial de recuperação.
let _onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  _onUnauthorized = typeof fn === "function" ? fn : null;
}

// S3.2 — resolve o header de autenticação para chamadas ao /api/admin.
// Prefere a sessão de usuário (Authorization: Bearer <token de login>) e cai
// para o X-Admin-Token de bancada quando não há sessão. Lança se faltarem
// ambos (UI redireciona para a tela de login).
function authHeaders() {
  const session = loadSession();
  if (session?.token) {
    return { Authorization: `Bearer ${session.token}` };
  }
  // SAAS-AUTH-RECOVERY-MODE-1 — o ADMIN_TOKEN só é usado quando o modo de
  // recuperação está EXPLICITAMENTE ativo; sem isso, nunca cai no X-Admin-Token
  // silencioso (força a tela de login).
  const { adminToken } = loadConfig();
  if (adminToken && isRecoveryActive()) {
    return { "X-Admin-Token": adminToken };
  }
  throw new ApiError("Autenticação ausente: faça login (ou ative o acesso de recuperação)", {
    status: 0,
  });
}

async function request(path, { method = "GET", auth = false, body, signal, headers: extraHeaders } = {}) {
  const headers = {
    Accept: "application/json",
  };
  if (auth) {
    Object.assign(headers, authHeaders());
  }
  // MAP-MANAGE-1E-B — headers extras opcionais (ex: X-Confirm-* p/ confirmação
  // forte em geo). Mesclados por último; segredo NUNCA vai em query string.
  if (extraHeaders) {
    Object.assign(headers, extraHeaders);
  }

  const fetchInit = { method, headers, signal };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    fetchInit.body = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(buildUrl(path), fetchInit);
  } catch (err) {
    // Network error, CORS, DNS, etc — fetch só rejeita aqui.
    if (err.name === "AbortError") throw err;
    throw new ApiError(`Falha de rede: ${err.message}`, { status: 0 });
  }

  // Parse JSON quando possível (mesmo em erro, para extrair "detail")
  let resBody = null;
  const ctype = res.headers.get("content-type") || "";
  if (ctype.includes("application/json")) {
    try {
      resBody = await res.json();
    } catch {
      resBody = null;
    }
  } else {
    try {
      resBody = await res.text();
    } catch {
      resBody = null;
    }
  }

  if (!res.ok) {
    const msg =
      (resBody && typeof resBody === "object" && resBody.detail) ||
      (typeof resBody === "string" && resBody) ||
      `HTTP ${res.status}`;
    // Para 422 (validação Pydantic), detail vem como array de objetos.
    // Achata em string legível.
    let finalMsg = msg;
    if (Array.isArray(msg)) {
      // Sprint 4.6.2 — inclui loc (nome do campo) p/ deixar erro mais útil.
      // Ex: 'heartbeat_interval_s: Extra inputs are not permitted' em vez de só o msg.
      finalMsg = msg
        .map((m) => {
          if (typeof m !== "object") return String(m);
          const locParts = Array.isArray(m.loc)
            ? m.loc.filter((x) => x !== "body" && (typeof x === "string" || typeof x === "number"))
            : [];
          const loc = locParts.join(".");
          const text = m.msg || JSON.stringify(m);
          return loc ? `${loc}: ${text}` : text;
        })
        .join("; ");
    } else if (msg && typeof msg === "object") {
      // Sprint 4.6.2 — detail customizado de templates é
      //   {error: 'invalid_payload', errors: {config_advanced: [pyrepr]}}
      // O api.js antigo retornava só msg.error → texto cru 'invalid_payload'.
      // Agora extraímos errors detalhados primeiro.
      if (msg.errors && typeof msg.errors === "object") {
        const lines = [];
        for (const [key, errArr] of Object.entries(msg.errors)) {
          if (Array.isArray(errArr)) {
            for (const item of errArr) {
              const parsed = _parsePydanticErrorRepr(item);
              lines.push(parsed.field
                ? `${key}.${parsed.field}: ${parsed.msg}`
                : `${key}: ${parsed.msg}`);
            }
          } else if (typeof errArr === "string") {
            lines.push(`${key}: ${errArr}`);
          }
        }
        if (lines.length > 0) {
          finalMsg = lines.join("\n");
        } else {
          finalMsg = msg.message || msg.error || JSON.stringify(msg);
        }
      } else {
        // Sprint 4.6.1 — detail customizado (ex: 409 confirm_risky_required vira
        // { error, message, risks }). Antes virava "[object Object]" em String(msg).
        finalMsg = msg.message || msg.error || JSON.stringify(msg);
      }
    }
    // SAAS-AUTH-RETIRE-ADMIN-TOKEN — 401 numa chamada autenticada COM sessão
    // Bearer = sessão expirada/inválida. Limpa a sessão e dispara o handler
    // (redirect a login), evitando o fallback silencioso para X-Admin-Token.
    // Bench (ADMIN_TOKEN sem sessão) mantém o erro inline (não desloga).
    if (res.status === 401 && auth && loadSession()?.token) {
      clearSession();
      if (_onUnauthorized) {
        try {
          _onUnauthorized();
        } catch {
          /* noop */
        }
      }
    }
    // S3.6 — 403: mensagem amigável de permissão (UX). O detalhe original do
    // backend continua disponível em err.body.detail para depuração.
    // NOC-UX-DEVICES-DETAIL-POLISH-1 — não mascarar o bloqueio de PRODUÇÃO: o guard
    // da denylist devolve reason=ota_protected/command_protected; mostra mensagem
    // específica em vez do genérico de permissão.
    if (res.status === 403) {
      const reason = resBody && typeof resBody === "object" ? resBody.reason : null;
      if (reason === "ota_protected" || reason === "command_protected") {
        finalMsg =
          "Dispositivo protegido (produção) — ação bloqueada pela denylist de segurança.";
      } else {
        finalMsg = "Seu usuário não tem permissão para esta ação.";
      }
    }
    throw new ApiError(String(finalMsg), { status: res.status, body: resBody });
  }

  return resBody;
}

// ─────────────────────────────────────────────────────────────────────────
// S3.2 — Autenticação de usuário (login/sessão multi-tenant)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Login (email + senha) no backend; persiste a sessão localmente e a retorna.
 * Lança ApiError em falha (401 credenciais inválidas, rede, etc.).
 */
export async function login(email, password) {
  const out = await request("/api/auth/login", {
    method: "POST",
    auth: false,
    body: { email, password },
  });
  return saveSession({
    token: out.token,
    email: out.email,
    role: out.role,
    tenantId: out.tenant_id,
    tenantName: out.tenant_name,
    fullName: out.full_name,
  });
}

/** Identidade do token de sessão atual (GET /api/auth/me). */
export async function fetchMe({ signal } = {}) {
  return request("/api/auth/me", { method: "GET", auth: true, signal });
}

/** Logout: avisa o backend (best-effort, stateless) e limpa a sessão local. */
export async function logout() {
  try {
    await request("/api/auth/logout", { method: "POST", auth: true });
  } catch {
    /* stateless — ignora erro de rede/401 */
  }
  clearSession();
}

/** S3.7 — troca a senha do próprio usuário (Bearer). Lança ApiError em 400/401. */
export async function changePassword(currentPassword, newPassword) {
  return request("/api/auth/change-password", {
    method: "POST",
    auth: true,
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

/** S3.7 — atualiza o perfil do próprio usuário (full_name). */
export async function updateMe(fullName) {
  return request("/api/auth/me", {
    method: "PATCH",
    auth: true,
    body: { full_name: fullName },
  });
}

// ── S3.8 — Gestão de contas de login (super_admin / platform admin) ──
/** Lista as contas de login (model User). */
export async function listAccounts({ signal } = {}) {
  return request("/api/admin/accounts", { method: "GET", auth: true, signal });
}
/** Tenants (id+nome) para o seletor da tela de contas (aceita Bearer super_admin). */
export async function listAccountTenants({ signal } = {}) {
  return request("/api/admin/accounts/tenants", { method: "GET", auth: true, signal });
}
/** Cria uma conta de login; retorna a conta + temp_password (mostrar 1x). */
export async function createAccount(payload) {
  return request("/api/admin/accounts", { method: "POST", auth: true, body: payload });
}
/** Edita full_name/role/tenant_id/is_active de uma conta. */
export async function updateAccount(id, payload) {
  if (!id) throw new ApiError("id ausente");
  return request(`/api/admin/accounts/${encodeURIComponent(id)}`, {
    method: "PATCH", auth: true, body: payload,
  });
}
/** Reseta a senha de uma conta; retorna temp_password (mostrar 1x). */
export async function resetAccountPassword(id) {
  if (!id) throw new ApiError("id ausente");
  return request(`/api/admin/accounts/${encodeURIComponent(id)}/reset-password`, {
    method: "POST", auth: true,
  });
}

// Sprint 4.6.2 — extrai 'loc' e 'msg' de uma string repr() de ErrorDict
// Pydantic. Exemplos de entrada:
//   "{'type': 'greater_than_equal', 'loc': ('heartbeat_interval',), 'msg': 'Input should be greater than or equal to 30', 'input': 5, ...}"
//   "{'type': 'literal_error', 'loc': ('relay_boot',), 'msg': \"Input should be 'restore', 'on' or 'off'\", ...}"
// Retorna {field, msg} — sempre uma string (fallback p/ a entrada original).
function _parsePydanticErrorRepr(s) {
  if (typeof s !== "string") return { field: "", msg: String(s) };
  // 'msg': '...'  OU  'msg': "..."
  // Captura tudo entre aspas, lidando com escape:
  const msgMatch = s.match(/'msg':\s*(["'])((?:\\.|(?!\1).)*)\1/);
  // 'loc': ('field', 'sub')  OU  'loc': ['body', 'field']
  const locMatch = s.match(/'loc':\s*[\(\[]([^\)\]]+)[\)\]]/);
  let field = "";
  if (locMatch) {
    field = locMatch[1]
      .split(",")
      .map((p) => p.trim().replace(/^['"]|['"]$/g, ""))
      .filter((p) => p && p !== "body");
    field = field.join(".");
  }
  const msg = msgMatch ? msgMatch[2] : s;
  return { field, msg };
}

// ====== Endpoints públicos ======

export async function getHealth({ signal } = {}) {
  return request("/health", { signal });
}

// SEC-DEFAULTS-WARN — resumo SANITIZADO de configs de segurança fracas/default.
// Nunca retorna valores de segredos (só code/severity/title/detail/remediation).
export async function getSecuritySummary({ signal } = {}) {
  return request("/api/admin/security/summary", { auth: true, signal });
}

// ENERGY-HISTORY-24H — histórico de energia (INA226) agregado por device.
// Lazy: chamado só ao abrir o painel Energia 24h na página Dispositivos.
// Device sem INA226 → { supported: false, points: [] } (painel some).
export async function getEnergyHistory(
  deviceId,
  { hours = 24, bucketMinutes = 5, signal } = {},
) {
  const qs = new URLSearchParams({
    hours: String(hours),
    bucket_minutes: String(bucketMinutes),
  });
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/energy-history?${qs}`,
    { auth: true, signal },
  );
}

// FW-STABILITY-SOAK-1 — histórico de estabilidade (heap/uptime/reboots) +
// veredito de risco, agregado de heartbeats_raw (read-only).
export async function getStabilityHistory(
  deviceId,
  { hours = 72, bucketMinutes = 30, signal } = {},
) {
  const qs = new URLSearchParams({
    hours: String(hours),
    bucket_minutes: String(bucketMinutes),
  });
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/stability-history?${qs}`,
    { auth: true, signal },
  );
}

export async function listDevices({
  includeArchived = false,
  authorization = null,
  signal,
} = {}) {
  // S3.11B — por padrão a lista traz só ativos (archived_at NULL) e só
  // 'authorized' (S3.11C). include_archived=true também mostra arquivados;
  // authorization=pending|blocked|all alimenta a fila de autorização.
  const params = new URLSearchParams();
  if (includeArchived) params.set("include_archived", "true");
  if (authorization) params.set("authorization", authorization);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return request(`/api/admin/devices${qs}`, { auth: true, signal });
}

export async function getDevice(deviceId, { signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  // device_id pode conter caracteres especiais — encode para o path
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}`, { auth: true, signal });
}

/**
 * S3.11B — arquiva (soft-delete) um dispositivo. NÃO apaga nada: marca
 * archived_at/archived_by/archive_reason e o device some das listas
 * operacionais. Histórico, telemetria, comandos, agenda, auditoria e
 * vínculos permanecem. Ação crítica: exige confirmação (senha do usuário
 * logado OU ADMIN_TOKEN na bancada). Só super_admin/bancada (backend 403).
 */
export async function archiveDevice(deviceId, { reason, confirm, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}/archive`, {
    method: "POST",
    auth: true,
    body: {
      ...(reason ? { reason } : {}),
      ...criticalConfirmFields(confirm),
    },
    signal,
  });
}

/** S3.11B — restaura um dispositivo arquivado (limpa os campos de archive). */
export async function restoreDevice(deviceId, { confirm, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}/restore`, {
    method: "POST",
    auth: true,
    body: { ...criticalConfirmFields(confirm) },
    signal,
  });
}

/**
 * F1 (recovery HMAC) — bootstrap-reset: limpa hmac_secret/pending/consumed no
 * backend. O device sem secret refaz bootstrap e recebe um secret NOVO, que o
 * firmware salva no NVS automaticamente. Caminho OFICIAL de reprovisionamento
 * HMAC (substitui o rotate+/savecloud manual). Só super_admin/bancada
 * (backend require_admin_token → 403). Destrutivo: usar só p/ recovery.
 */
export async function resetDeviceBootstrap(deviceId, { confirm, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}/bootstrap-reset`, {
    method: "POST",
    auth: true,
    body: { ...criticalConfirmFields(confirm) },
    signal,
  });
}

/**
 * S3.11C — autoriza um dispositivo (pending|blocked → authorized): passa a ser
 * operacional. Ação crítica (senha do usuário OU ADMIN_TOKEN). Só super_admin/
 * bancada (backend 403). Ao autorizar, o backend vincula binding pendente +
 * aplica template diferido, se houver.
 */
export async function authorizeDevice(deviceId, { reason, confirm, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}/authorize`, {
    method: "POST",
    auth: true,
    body: { ...(reason ? { reason } : {}), ...criticalConfirmFields(confirm) },
    signal,
  });
}

/** S3.11C — bloqueia um dispositivo (pending|authorized → blocked). */
export async function blockDevice(deviceId, { reason, confirm, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}/block`, {
    method: "POST",
    auth: true,
    body: { ...(reason ? { reason } : {}), ...criticalConfirmFields(confirm) },
    signal,
  });
}

/** S3.11C — revoga a autorização (authorized → pending): volta para a fila. */
export async function revokeDevice(deviceId, { reason, confirm, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}/revoke`, {
    method: "POST",
    auth: true,
    body: { ...(reason ? { reason } : {}), ...criticalConfirmFields(confirm) },
    signal,
  });
}

// ====== PROTECTED-DEVICES-MANAGE-1C/1D: proteção dinâmica de device ======
// Estado efetivo = ENV OR DB. enable/disable são PLATFORM-ONLY + confirmação forte +
// reason OBRIGATÓRIO no backend; disable só funciona para source='manual' (env/
// production/reference são imutáveis → 403). Backend é a fonte da verdade.

/** Estado de proteção efetiva do device (GET — também exposto em center.protection). */
export async function getDeviceProtection(deviceId, { signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  return request(`/api/admin/devices/${encodeURIComponent(deviceId)}/protection`, {
    auth: true,
    signal,
  });
}

/** Liga a proteção MANUAL do device (platform-only + confirmação forte; reason obrigatório). */
export async function enableDeviceProtection(deviceId, { reason, confirm, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  return request(`/api/admin/devices/${encodeURIComponent(deviceId)}/protection/enable`, {
    method: "POST",
    auth: true,
    body: { reason, ...criticalConfirmFields(confirm) },
    signal,
  });
}

/** Desliga a proteção MANUAL (source imutável env/production/reference → 403). */
export async function disableDeviceProtection(deviceId, { reason, confirm, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  return request(`/api/admin/devices/${encodeURIComponent(deviceId)}/protection/disable`, {
    method: "POST",
    auth: true,
    body: { reason, ...criticalConfirmFields(confirm) },
    signal,
  });
}

// ====== Sprint 2 Etapa 3d: comandos ======

/**
 * Enfileira um comando relay_reboot.
 *
 * Backend (Etapa 2) valida:
 *  - type ∈ MVP Sprint 2 (Pydantic Literal)
 *  - payload.duration_s ∈ [1, 300]
 *  - ttl_seconds ∈ [30, 86400]
 *
 * Retorna o objeto CommandOut completo (id, status='pending', …).
 *
 * @param {string} deviceId
 * @param {number} durationS  — segundos do desligamento (1..300)
 * @param {{signal?: AbortSignal}} opts
 */
/**
 * S3.10A — campos de confirmação de ação crítica para o body do request.
 * Usuário logado (Bearer) → confirm_password; bancada (X-Admin-Token) →
 * confirm_admin_token. Vazio se não houver segredo (ação não-crítica). A
 * credencial NÃO é persistida — só compõe este body e segue no request.
 */
function criticalConfirmFields(confirm) {
  if (!confirm) return {};
  if (loadSession()?.token) return { confirm_password: confirm };
  // SAAS-AUTH-BEARER-ENFORCEMENT — ADMIN_TOKEN como confirmação só em recovery
  // explícito (nunca silencioso fora de recuperação).
  if (loadConfig().adminToken && isRecoveryActive()) return { confirm_admin_token: confirm };
  return {};
}

// MAP-MANAGE-1E-B — espelho de criticalConfirmFields como HEADERS, para rotas
// que recebem a confirmação forte via X-Confirm-* (geo). Mesma escolha por modo
// de auth: sessão→senha, bancada→ADMIN_TOKEN. Sem confirm → nenhum header.
function criticalConfirmHeaders(confirm) {
  if (!confirm) return {};
  if (loadSession()?.token) return { "X-Confirm-Password": confirm };
  // SAAS-AUTH-BEARER-ENFORCEMENT — ADMIN_TOKEN como confirmação só em recovery.
  if (loadConfig().adminToken && isRecoveryActive()) return { "X-Confirm-Admin-Token": confirm };
  return {};
}

export async function enqueueRelayReboot(deviceId, durationS, { signal, confirm } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const dur = Number(durationS);
  if (!Number.isInteger(dur) || dur < 1 || dur > 300) {
    throw new ApiError("duration_s deve ser inteiro entre 1 e 300");
  }
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}/commands`, {
    method: "POST",
    auth: true,
    body: {
      type: "relay_reboot",
      payload: { duration_s: dur },
      ...criticalConfirmFields(confirm),
    },
    signal,
  });
}

/**
 * Enfileira um comando reboot (do ESP32).
 *
 * Sprint 2 Etapa 3g — payload vazio. Validação no backend (Etapa 2)
 * já confirmava 'reboot' como type permitido. Firmware (Etapa 3g)
 * trata com cooldown de 5 minutos + ack-before-restart.
 *
 * @param {string} deviceId
 * @param {{signal?: AbortSignal}} opts
 */
export async function enqueueReboot(deviceId, { signal, confirm } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}/commands`, {
    method: "POST",
    auth: true,
    body: {
      type: "reboot",
      payload: {},
      ...criticalConfirmFields(confirm),
    },
    signal,
  });
}

/**
 * Busca o estado atual de um comando.
 * Usado para polling pós-POST (pending → delivered → acked).
 *
 * @param {string} deviceId
 * @param {string} commandId  — UUID retornado pelo enqueue
 */
export async function getCommand(deviceId, commandId, { signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  if (!commandId) throw new ApiError("command_id ausente");
  const d = encodeURIComponent(deviceId);
  const c = encodeURIComponent(commandId);
  return request(`/api/admin/devices/${d}/commands/${c}`, {
    auth: true,
    signal,
  });
}

/**
 * Lista comandos de um device (mais recentes primeiro).
 *
 * Sprint 2 Etapa 3e — usado pelo CommandHistory no DeviceDetail.
 * Sem filtros nem paginação (Etapa 3e escopo); apenas `limit`.
 *
 * Backend (Etapa 2) já valida: limit ∈ [1, 200].
 *
 * @param {string} deviceId
 * @param {{limit?: number, signal?: AbortSignal}} opts
 */
export async function listCommands(deviceId, { limit = 20, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const d = encodeURIComponent(deviceId);
  const qs = `?limit=${encodeURIComponent(limit)}`;
  return request(`/api/admin/devices/${d}/commands${qs}`, {
    auth: true,
    signal,
  });
}

// ====== S7.5C-C: OTA Events History ======
/**
 * Lista histórico de eventos OTA do device (heartbeats_raw → ota_events).
 * Já vem deduplicado pelo backend com chave (event, ts, fw, progress, reason).
 * @param {string} deviceId — chave humana do device
 * @param {{since?: string, limit?: number, signal?: AbortSignal}} opts
 *   - since: ISO 8601 datetime; default = sem corte
 *   - limit: 1–200, default 50
 * @returns {Promise<{device_id: string, total: number, items: Array<object>}>}
 */
export async function listDeviceOtaEvents(deviceId, { since, limit = 50, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const d = encodeURIComponent(deviceId);
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (since) params.set("since", since);
  return request(`/api/admin/devices/${d}/ota-events?${params.toString()}`, {
    auth: true,
    signal,
  });
}

// ====== Sprint 3.3/3.4: Rate limit metrics ======

/**
 * Sprint 3.3 — Snapshot das métricas de rate limit (globais, em RAM).
 *
 * Retorna:
 *   rate_limited_count      — total over-limit desde restart do worker
 *   last_rate_limit_reason  — string com endpoint + chave + limite
 *   last_rate_limit_at      — epoch UTC da última ocorrência (0 se nenhuma)
 *   by_label                — breakdown {label: count}, labels:
 *                             hb:device, hb:ip, hb:both, bootstrap_device, etc.
 *
 * Métricas resetam no restart de cada worker. Em prod multi-worker, esta
 * chamada retorna o snapshot do worker que recebeu — pode variar.
 */
export async function getRateLimitMetrics({ signal } = {}) {
  return request("/api/admin/health/metrics", { auth: true, signal });
}

/**
 * Sprint 3.3 — Reseta as métricas em RAM.
 *
 * Operação destrutiva (apaga histórico). UI deve confirmar antes de
 * chamar. Útil após investigação ou mudança de configuração.
 *
 * Em prod multi-worker, reseta apenas o worker que recebeu (não atômico
 * entre workers). Aceitável: métricas são best-effort.
 */
export async function resetRateLimitMetrics({ signal } = {}) {
  return request("/api/admin/health/metrics/reset", {
    method: "POST",
    auth: true,
    signal,
  });
}

// ====== Sprint 3.5: Firmware OTA ======

/**
 * Upload multipart de um .bin de firmware.
 *
 * @param {{version: string, notes?: string, file: File}} params
 * @param {{onProgress?: function, signal?: AbortSignal}} opts
 * @returns FirmwareReleaseOut (id, version, sha256, size_bytes, ...)
 */
export async function uploadFirmware({ version, notes, file }, { signal } = {}) {
  if (!version) throw new ApiError("version obrigatória");
  if (!file) throw new ApiError("file obrigatório");

  const form = new FormData();
  form.append("version", version);
  if (notes) form.append("notes", notes);
  form.append("file", file);

  // request() não suporta FormData direto (seta Content-Type JSON).
  // Faz fetch direto, reusando authHeaders() (Bearer ou X-Admin-Token).
  const url = buildUrl("/api/admin/firmware");
  const res = await fetch(url, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
    signal,
  });
  let body = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    let msg = body?.detail || body?.message || `HTTP ${res.status}`;
    if (Array.isArray(msg)) {
      msg = msg
        .map((m) => (typeof m === "object" ? (m.msg || JSON.stringify(m)) : String(m)))
        .join("; ");
    }
    throw new ApiError(String(msg), { status: res.status, body });
  }
  return body;
}

export async function listFirmwareReleases({ limit = 50, include_deprecated = false, signal } = {}) {
  const qs = new URLSearchParams();
  qs.set("limit", String(limit));
  if (include_deprecated) qs.set("include_deprecated", "true");
  return request(`/api/admin/firmware?${qs.toString()}`, { auth: true, signal });
}

export async function getFirmwareRelease(releaseId, { signal } = {}) {
  if (!releaseId) throw new ApiError("release_id ausente");
  return request(`/api/admin/firmware/${releaseId}`, { auth: true, signal });
}

export async function deleteFirmwareRelease(releaseId, { signal } = {}) {
  if (!releaseId) throw new ApiError("release_id ausente");
  return request(`/api/admin/firmware/${releaseId}`, {
    method: "DELETE",
    auth: true,
    signal,
  });
}

/**
 * Dispara OTA em um device específico.
 *
 * @param {string} deviceId
 * @param {string} releaseId
 * @param {{ttl_seconds?: number, signal?: AbortSignal}} opts
 */
export async function triggerOta(deviceId, releaseId, { ttl_seconds, signal, confirm } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  if (!releaseId) throw new ApiError("release_id ausente");
  const d = encodeURIComponent(deviceId);
  const body = { release_id: releaseId };
  if (ttl_seconds) body.ttl_seconds = ttl_seconds;
  Object.assign(body, criticalConfirmFields(confirm));
  return request(`/api/admin/devices/${d}/ota`, {
    method: "POST",
    auth: true,
    body,
    signal,
  });
}

// ====== Sprint 3.6: Fleet OTA management ======

/**
 * Snapshot agregado por fw_version.
 * Retorna {versions:[{version,total,online,offline,ota_pending,ota_failed}],
 *          generated_at, online_window_s, ota_failed_window_s}
 */
export async function getFleetVersions({ signal } = {}) {
  return request("/api/admin/fleet/versions", { auth: true, signal });
}

/**
 * NOC-PROD-UI-1 — Painel de Produção (read-only): release/segurança/OTA por
 * device + denylist OTA + rollouts não-terminais. Sem mutação.
 */
export async function getFleetProduction({ signal } = {}) {
  return request("/api/admin/fleet/production-status", { auth: true, signal });
}

/**
 * Lista detalhada de devices com estado OTA.
 *
 * @param {{fw_version?: string, only_failed?: boolean, limit?: number}} opts
 */
export async function getFleetOtaStatus(
  { fw_version, only_failed = false, limit = 200, signal } = {}
) {
  const qs = new URLSearchParams();
  if (fw_version !== undefined && fw_version !== null) {
    qs.set("fw_version", fw_version);
  }
  if (only_failed) qs.set("only_failed", "true");
  if (limit !== 200) qs.set("limit", String(limit));
  const q = qs.toString();
  return request(`/api/admin/fleet/ota-status${q ? "?" + q : ""}`, {
    auth: true,
    signal,
  });
}

/**
 * Dispara rollout em massa.
 *
 * @param {string} releaseId
 * @param {{target_version_before?: string, limit?: number, ttl_seconds?: number}} body
 */
export async function triggerRollout(releaseId, body, { signal, confirm } = {}) {
  if (!releaseId) throw new ApiError("release_id ausente");
  return request(`/api/admin/firmware/${releaseId}/rollout`, {
    method: "POST",
    auth: true,
    body: { ...(body || {}), ...criticalConfirmFields(confirm) },
    signal,
  });
}

// ====== Sprint 3.7 Etapa 2: Rollout audit ======

/**
 * Lista batches de rollout (mais recentes primeiro).
 *
 * @param {{release_id?: string, since?: string, before?: string, limit?: number}} opts
 * @returns {{batches: Array}}
 */
export async function listRolloutBatches(
  { release_id, since, before, limit = 50, signal } = {}
) {
  const qs = new URLSearchParams();
  if (release_id) qs.set("release_id", release_id);
  if (since) qs.set("since", since);
  if (before) qs.set("before", before);
  if (limit !== 50) qs.set("limit", String(limit));
  const q = qs.toString();
  return request(`/api/admin/rollout-batches${q ? "?" + q : ""}`, {
    auth: true,
    signal,
  });
}

/**
 * Detalhe de um batch específico.
 */
export async function getRolloutBatch(batchId, { signal } = {}) {
  if (!batchId) throw new ApiError("batch_id ausente");
  return request(`/api/admin/rollout-batches/${batchId}`, {
    auth: true,
    signal,
  });
}

/**
 * Comandos linkados ao batch (drill-down).
 */
export async function getRolloutBatchCommands(batchId, { limit = 500, signal } = {}) {
  if (!batchId) throw new ApiError("batch_id ausente");
  const qs = new URLSearchParams();
  if (limit !== 500) qs.set("limit", String(limit));
  const q = qs.toString();
  return request(
    `/api/admin/rollout-batches/${batchId}/commands${q ? "?" + q : ""}`,
    { auth: true, signal }
  );
}

// ====== Sprint 3.7 Etapa 3: Rollout progressivo (waves) ======

/**
 * Cria rollout progressivo. Wave 1 é armada imediatamente.
 *
 * @param {string} releaseId
 * @param {{target_version_before?: string, waves: number[], ttl_seconds?: number}} body
 *   waves: array estritamente crescente terminando em 100. Ex: [5,20,50,100]
 */
export async function triggerRolloutProgressive(releaseId, body, { signal, confirm } = {}) {
  if (!releaseId) throw new ApiError("release_id ausente");
  return request(`/api/admin/firmware/${releaseId}/rollout-progressive`, {
    method: "POST",
    auth: true,
    body: { ...(body || {}), ...criticalConfirmFields(confirm) },
    signal,
  });
}

export async function pauseRolloutBatch(batchId, { signal } = {}) {
  return request(`/api/admin/rollout-batches/${batchId}/pause`, {
    method: "POST",
    auth: true,
    signal,
  });
}

export async function resumeRolloutBatch(batchId, { signal } = {}) {
  return request(`/api/admin/rollout-batches/${batchId}/resume`, {
    method: "POST",
    auth: true,
    signal,
  });
}

export async function cancelRolloutBatch(batchId, { signal } = {}) {
  return request(`/api/admin/rollout-batches/${batchId}/cancel`, {
    method: "POST",
    auth: true,
    signal,
  });
}

/**
 * Cria batch INVERSO para devices atualmente no release deste batch.
 *
 * @param {string} batchId — batch original (será marcado como rolled_back)
 * @param {{previous_release_id: string, ttl_seconds?: number}} body
 */
export async function rollbackRolloutBatch(batchId, body, { signal, confirm } = {}) {
  return request(`/api/admin/rollout-batches/${batchId}/rollback`, {
    method: "POST",
    auth: true,
    body: { ...(body || {}), ...criticalConfirmFields(confirm) },
    signal,
  });
}

// ====== Sprint 3.7 closeout: Deprecation ======

/**
 * Marca release como deprecated. Idempotente.
 * @param {string} releaseId
 * @param {{reason?: string}} body
 */
export async function deprecateFirmwareRelease(releaseId, body = {}, { signal } = {}) {
  return request(`/api/admin/firmware/${releaseId}/deprecate`, {
    method: "PATCH",
    auth: true,
    body,
    signal,
  });
}

/**
 * Desfaz deprecate. Idempotente.
 */
export async function undeprecateFirmwareRelease(releaseId, { signal } = {}) {
  return request(`/api/admin/firmware/${releaseId}/undeprecate`, {
    method: "PATCH",
    auth: true,
    signal,
  });
}

// ====== Sprint 3.8: Dashboard Enterprise ======

/**
 * GET /api/admin/dashboard/summary — 9 cards.
 */
export async function getDashboardSummary({ signal } = {}) {
  return request(`/api/admin/dashboard/summary`, { auth: true, signal });
}

/**
 * GET /api/admin/dashboard/ota-health — timeline diária OTA.
 * @param {{days?: number}} opts — 1..90, default 14
 */
export async function getOtaHealth({ days = 14, signal } = {}) {
  const qs = new URLSearchParams();
  if (days !== 14) qs.set("days", String(days));
  const q = qs.toString();
  return request(`/api/admin/dashboard/ota-health${q ? "?" + q : ""}`, {
    auth: true,
    signal,
  });
}

/**
 * GET /api/admin/dashboard/fleet-health — distribuição fw_version,
 * online history, devices com erro OTA, últimos comandos.
 */
export async function getFleetHealth({ signal } = {}) {
  return request(`/api/admin/dashboard/fleet-health`, {
    auth: true,
    signal,
  });
}

/**
 * Sprint 3.8.1 — POST /maintenance/reconcile-commands.
 */
export async function reconcileCommands({ signal } = {}) {
  return request(`/api/admin/maintenance/reconcile-commands`, {
    method: "POST",
    auth: true,
    body: {},
    signal,
  });
}

/**
 * Sprint 3.8.1 — Constrói URL do WebSocket /ws/dashboard.
 *
 * Reusa apiUrl + adminToken do loadConfig (mesma fonte do request()).
 * Browser não permite header customizado em new WebSocket(),
 * então o token vai via query string `?token=`.
 */
export function getDashboardWsUrl() {
  const { apiUrl, adminToken } = loadConfig();
  // SAAS-AUTH-RETIRE-ADMIN-TOKEN — prefere o token de SESSÃO Bearer (super_admin)
  // e só cai p/ o ADMIN_TOKEN de bancada. O backend /ws/dashboard valida sessão
  // de super_admin OU o ADMIN_TOKEN. Browser não permite header customizado em
  // WebSocket nativo, então o token segue na query string.
  const wsToken =
    loadSession()?.token || (isRecoveryActive() ? adminToken : "") || "";
  // Converte http(s) → ws(s). INSTALLER-LOGIN-NETWORK-DIAG-1 — mesmo guard anti
  // mixed-content do buildUrl: numa página https, base http:// vira same-origin
  // (wss no mesmo host via Caddy) em vez de um ws:// bloqueado.
  const proto =
    (typeof window !== "undefined" && window.location && window.location.protocol) || "";
  let wsBase = resolveApiBase(apiUrl, proto);
  if (wsBase.startsWith("https://")) wsBase = "wss://" + wsBase.slice(8);
  else if (wsBase.startsWith("http://")) wsBase = "ws://" + wsBase.slice(7);
  else if (!wsBase) {
    const loc = window.location;
    const scheme = loc.protocol === "https:" ? "wss:" : "ws:";
    wsBase = `${scheme}//${loc.host}`;
  }
  // Remove barra final, se houver
  wsBase = wsBase.replace(/\/+$/, "");
  return `${wsBase}/ws/dashboard?token=${encodeURIComponent(wsToken)}`;
}

// ============================================================================
// Sprint 4.0 — Fleet Management
// ============================================================================

/** GET /api/admin/customers */
export async function listCustomers({ signal } = {}) {
  return request(`/api/admin/customers`, { auth: true, signal });
}

/** POST /api/admin/customers */
export async function createCustomer(body, { signal } = {}) {
  return request(`/api/admin/customers`, {
    method: "POST",
    auth: true,
    body,
    signal,
  });
}

/** PUT /api/admin/customers/{id} */
export async function updateCustomer(customerId, body, { signal } = {}) {
  return request(`/api/admin/customers/${customerId}`, {
    method: "PUT",
    auth: true,
    body,
    signal,
  });
}

/** DELETE /api/admin/customers/{id} */
export async function deleteCustomer(customerId, { signal } = {}) {
  return request(`/api/admin/customers/${customerId}`, {
    method: "DELETE",
    auth: true,
    signal,
  });
}

/** POST /api/admin/customers/migrate-from-codes?dry_run=true|false */
export async function migrateFromCodes({ dryRun = false, signal } = {}) {
  const qs = dryRun ? "?dry_run=true" : "";
  return request(`/api/admin/customers/migrate-from-codes${qs}`, {
    method: "POST",
    auth: true,
    body: {},
    signal,
  });
}

/** GET /api/admin/groups?customer_id=... */
export async function listGroups({ customerId = null, signal } = {}) {
  const qs = customerId ? `?customer_id=${encodeURIComponent(customerId)}` : "";
  return request(`/api/admin/groups${qs}`, { auth: true, signal });
}

export async function createGroup(body, { signal } = {}) {
  return request(`/api/admin/groups`, {
    method: "POST", auth: true, body, signal,
  });
}

export async function updateGroup(groupId, body, { signal } = {}) {
  return request(`/api/admin/groups/${groupId}`, {
    method: "PUT", auth: true, body, signal,
  });
}

export async function deleteGroup(groupId, { signal } = {}) {
  return request(`/api/admin/groups/${groupId}`, {
    method: "DELETE", auth: true, signal,
  });
}

/** GET /api/admin/tags */
export async function listTags({ signal } = {}) {
  return request(`/api/admin/tags`, { auth: true, signal });
}

export async function createTag(body, { signal } = {}) {
  return request(`/api/admin/tags`, {
    method: "POST", auth: true, body, signal,
  });
}

export async function deleteTag(tagId, { signal } = {}) {
  return request(`/api/admin/tags/${tagId}`, {
    method: "DELETE", auth: true, signal,
  });
}

/** POST /api/admin/devices/{id}/tags — substitui conjunto de tags */
export async function setDeviceTags(deviceId, tagIds, { signal } = {}) {
  return request(`/api/admin/devices/${deviceId}/tags`, {
    method: "POST",
    auth: true,
    body: { tag_ids: tagIds },
    signal,
  });
}

/** POST /api/admin/devices/{id}/group */
export async function setDeviceGroup(deviceId, groupId, { signal } = {}) {
  return request(`/api/admin/devices/${deviceId}/group`, {
    method: "POST",
    auth: true,
    body: { group_id: groupId },
    signal,
  });
}

/** POST /api/admin/devices/{id}/location */
export async function setDeviceLocation(deviceId, location, { signal, confirm } = {}) {
  return request(`/api/admin/devices/${deviceId}/location`, {
    method: "POST",
    auth: true,
    body: location,
    // MAP-MANAGE-1E-B — confirmação forte (device protegido) via header.
    headers: criticalConfirmHeaders(confirm),
    signal,
  });
}

/** GET /api/admin/devices/{id}/tags */
export async function getDeviceTags(deviceId, { signal } = {}) {
  return request(`/api/admin/devices/${deviceId}/tags`, {
    auth: true,
    signal,
  });
}

/**
 * GET /api/admin/devices com filtros Sprint 4.0.
 *
 * Filtros (todos opcionais):
 *   customer_id, group_id, fw_version, status, rssi_below
 *   tags: array — vai como ?tag=a&tag=b
 */
export async function listDevicesFiltered(filters = {}, { signal } = {}) {
  const params = new URLSearchParams();
  if (filters.customer_id) params.append("customer_id", filters.customer_id);
  if (filters.group_id) params.append("group_id", filters.group_id);
  if (filters.fw_version) params.append("fw_version", filters.fw_version);
  if (filters.status) params.append("status", filters.status);
  if (filters.rssi_below !== undefined && filters.rssi_below !== null && filters.rssi_below !== "") {
    params.append("rssi_below", String(filters.rssi_below));
  }
  if (Array.isArray(filters.tags)) {
    for (const t of filters.tags) {
      if (t) params.append("tag", t);
    }
  }
  // Sprint 4.6.5 — busca textual e limit
  if (filters.search) params.append("search", filters.search);
  if (filters.limit !== undefined && filters.limit !== null) {
    params.append("limit", String(filters.limit));
  }
  const qs = params.toString();
  return request(`/api/admin/devices${qs ? `?${qs}` : ""}`, {
    auth: true,
    signal,
  });
}

/**
 * Sprint 4.6.5 — Alias com nome amigável para a tela "Aplicar Template".
 * Aceita { search, status, group_id, customer_id, limit, ... }.
 */
export async function listAllDevices(filters = {}, opts = {}) {
  return listDevicesFiltered(filters, opts);
}

// ============================================================================
// Sprint 4.1 — Fleet Map
// ============================================================================

/** GET /api/admin/fleet-map/devices */
export async function getFleetMapDevices({ signal } = {}) {
  return request(`/api/admin/fleet-map/devices`, { auth: true, signal });
}

/** GET /api/admin/fleet-map/stats */
export async function getFleetMapStats({ signal } = {}) {
  return request(`/api/admin/fleet-map/stats`, { auth: true, signal });
}

/** POST /api/admin/devices/{id}/geo */
export async function setDeviceGeo(deviceId, geo, { signal, confirm } = {}) {
  return request(`/api/admin/devices/${deviceId}/geo`, {
    method: "POST",
    auth: true,
    body: geo,
    // MAP-MANAGE-1E-B — confirmação forte (device protegido) via header.
    headers: criticalConfirmHeaders(confirm),
    signal,
  });
}

// ============================================================================
// Sprint 4.1.1 — Bulk operations + helpers de ações no Fleet Map
// ============================================================================

/** POST /api/admin/fleet-map/bulk-command */
export async function bulkCommand(deviceIds, type, { payload = {}, ttl_seconds, signal, confirm } = {}) {
  return request(`/api/admin/fleet-map/bulk-command`, {
    method: "POST",
    auth: true,
    body: { device_ids: deviceIds, type, payload, ttl_seconds, ...criticalConfirmFields(confirm) },
    signal,
  });
}

/** POST /api/admin/fleet-map/bulk-ota */
export async function bulkOta(deviceIds, releaseId, { ttl_seconds, signal, confirm } = {}) {
  return request(`/api/admin/fleet-map/bulk-ota`, {
    method: "POST",
    auth: true,
    body: { device_ids: deviceIds, release_id: releaseId, ttl_seconds, ...criticalConfirmFields(confirm) },
    signal,
  });
}

/** POST /api/admin/fleet-map/bulk-tag — Sprint 4.1.2 */
export async function bulkTag(deviceIds, tagId, action = "add", { signal } = {}) {
  return request(`/api/admin/fleet-map/bulk-tag`, {
    method: "POST",
    auth: true,
    body: { device_ids: deviceIds, tag_id: tagId, action },
    signal,
  });
}

/** POST /api/admin/fleet-map/bulk-group — Sprint 4.1.2 */
export async function bulkGroup(deviceIds, groupId, { signal } = {}) {
  return request(`/api/admin/fleet-map/bulk-group`, {
    method: "POST",
    auth: true,
    body: { device_ids: deviceIds, group_id: groupId },
    signal,
  });
}

// ============================================================================
// Sprint 4.2C — ISP GIS
// ============================================================================

/** GET /api/admin/fleet-regions */
export async function listFleetRegions({ signal } = {}) {
  return request(`/api/admin/fleet-regions`, { method: "GET", auth: true, signal });
}

/** POST /api/admin/fleet-regions */
export async function createFleetRegion(body, { signal } = {}) {
  return request(`/api/admin/fleet-regions`, {
    method: "POST", auth: true, body, signal,
  });
}

/** PUT /api/admin/fleet-regions/{id} */
export async function updateFleetRegion(id, body, { signal } = {}) {
  return request(`/api/admin/fleet-regions/${encodeURIComponent(id)}`, {
    method: "PUT", auth: true, body, signal,
  });
}

/** DELETE /api/admin/fleet-regions/{id} */
export async function deleteFleetRegion(id, { signal } = {}) {
  return request(`/api/admin/fleet-regions/${encodeURIComponent(id)}`, {
    method: "DELETE", auth: true, signal,
  });
}

/** GET /api/admin/fleet-regions/{id}/devices */
export async function getRegionDevices(id, { signal } = {}) {
  return request(`/api/admin/fleet-regions/${encodeURIComponent(id)}/devices`, {
    method: "GET", auth: true, signal,
  });
}

/** GET /api/admin/network-nodes */
export async function listNetworkNodes({ signal } = {}) {
  return request(`/api/admin/network-nodes`, { method: "GET", auth: true, signal });
}

/** POST /api/admin/network-nodes */
export async function createNetworkNode(body, { signal } = {}) {
  return request(`/api/admin/network-nodes`, {
    method: "POST", auth: true, body, signal,
  });
}

/** PUT /api/admin/network-nodes/{id} */
export async function updateNetworkNode(id, body, { signal } = {}) {
  return request(`/api/admin/network-nodes/${encodeURIComponent(id)}`, {
    method: "PUT", auth: true, body, signal,
  });
}

/** DELETE /api/admin/network-nodes/{id} */
export async function deleteNetworkNode(id, { signal } = {}) {
  return request(`/api/admin/network-nodes/${encodeURIComponent(id)}`, {
    method: "DELETE", auth: true, signal,
  });
}

/** GET /api/admin/devices/{id}/network-links */
export async function listDeviceLinks(deviceId, { signal } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/network-links`,
    { method: "GET", auth: true, signal },
  );
}

/** POST /api/admin/devices/{id}/network-link */
export async function createDeviceLink(deviceId, body, { signal } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/network-link`,
    { method: "POST", auth: true, body, signal },
  );
}

/** DELETE /api/admin/devices/{id}/network-link/{link_id} */
export async function deleteDeviceLink(deviceId, linkId, { signal } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/network-link/${encodeURIComponent(linkId)}`,
    { method: "DELETE", auth: true, signal },
  );
}

/** GET /api/admin/external-integrations */
export async function listExternalIntegrations({ signal } = {}) {
  return request(`/api/admin/external-integrations`, {
    method: "GET", auth: true, signal,
  });
}

/** POST /api/admin/external-integrations */
export async function createExternalIntegration(body, { signal } = {}) {
  return request(`/api/admin/external-integrations`, {
    method: "POST", auth: true, body, signal,
  });
}

// ============================================================================
// Sprint 4.2D — IXC Integration
// ============================================================================

/** GET /api/admin/integrations/ixc — devolve null se não configurado (404) */
export async function getIxcConfig({ signal } = {}) {
  try {
    return await request(`/api/admin/integrations/ixc`, {
      method: "GET", auth: true, signal,
    });
  } catch (e) {
    // 404 = não configurado; transforma em null
    if (String(e?.message || "").includes("404")) return null;
    throw e;
  }
}

/** POST /api/admin/integrations/ixc */
export async function createIxcConfig(body, { signal } = {}) {
  return request(`/api/admin/integrations/ixc`, {
    method: "POST", auth: true, body, signal,
  });
}

/** PUT /api/admin/integrations/ixc */
export async function updateIxcConfig(body, { signal } = {}) {
  return request(`/api/admin/integrations/ixc`, {
    method: "PUT", auth: true, body, signal,
  });
}

/** DELETE /api/admin/integrations/ixc */
export async function deleteIxcConfig({ signal } = {}) {
  return request(`/api/admin/integrations/ixc`, {
    method: "DELETE", auth: true, signal,
  });
}

/** POST /api/admin/integrations/ixc/test */
export async function testIxcConnection({ signal } = {}) {
  return request(`/api/admin/integrations/ixc/test`, {
    method: "POST", auth: true, body: {}, signal,
  });
}

/** POST /api/admin/integrations/ixc/sync/customers */
export async function syncIxcCustomers({ signal } = {}) {
  return request(`/api/admin/integrations/ixc/sync/customers`, {
    method: "POST", auth: true, body: {}, signal,
  });
}

/** POST /api/admin/integrations/ixc/sync/infra */
export async function syncIxcInfra({ signal } = {}) {
  return request(`/api/admin/integrations/ixc/sync/infra`, {
    method: "POST", auth: true, body: {}, signal,
  });
}

/** POST /api/admin/integrations/ixc/sync/full */
export async function syncIxcFull({ signal } = {}) {
  return request(`/api/admin/integrations/ixc/sync/full`, {
    method: "POST", auth: true, body: {}, signal,
  });
}

/** GET /api/admin/integrations/ixc/logs */
export async function listIxcLogs({ limit = 50, signal } = {}) {
  return request(`/api/admin/integrations/ixc/logs?limit=${limit}`, {
    method: "GET", auth: true, signal,
  });
}

/** GET /api/admin/customers/map */
export async function listCustomersForMap({
  city, status, onlyWithCoords, signal,
} = {}) {
  const q = new URLSearchParams();
  if (city) q.set("city", city);
  if (status) q.set("status", status);
  if (onlyWithCoords) q.set("only_with_coords", "true");
  const qs = q.toString() ? `?${q}` : "";
  return request(`/api/admin/customers/map${qs}`, {
    method: "GET", auth: true, signal,
  });
}

/** POST /api/admin/devices/{id}/customer-link */
export async function linkDeviceToCustomer(deviceId, body, { signal } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/customer-link`,
    { method: "POST", auth: true, body, signal },
  );
}

/** DELETE /api/admin/devices/{id}/customer-link */
export async function unlinkDeviceCustomer(deviceId, { signal } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/customer-link`,
    { method: "DELETE", auth: true, signal },
  );
}

// ============================================================================
// Sprint 4.4 — Remote Config Center
// ============================================================================

/** POST /api/admin/devices/{id}/config/wifi */
export async function configWifi(deviceId, body, { signal, confirm } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/config/wifi`,
    { method: "POST", auth: true,
      body: { ...body, ...criticalConfirmFields(confirm) }, signal },
  );
}

/** POST /api/admin/devices/{id}/config/network */
export async function configNetwork(deviceId, body, { signal, confirm } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/config/network`,
    { method: "POST", auth: true,
      body: { ...body, ...criticalConfirmFields(confirm) }, signal },
  );
}

/** POST /api/admin/devices/{id}/config/management */
export async function configManagement(deviceId, body, { signal, confirm } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/config/management`,
    { method: "POST", auth: true,
      body: { ...body, ...criticalConfirmFields(confirm) }, signal },
  );
}

/** POST /api/admin/devices/{id}/config/watchdog */
export async function configWatchdog(deviceId, body, { signal, confirm } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/config/watchdog`,
    { method: "POST", auth: true,
      body: { ...body, ...criticalConfirmFields(confirm) }, signal },
  );
}

/** POST /api/admin/devices/{id}/config/schedule */
export async function configSchedule(deviceId, body, { signal, confirm } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/config/schedule`,
    { method: "POST", auth: true,
      body: { ...body, ...criticalConfirmFields(confirm) }, signal },
  );
}

/** Sprint 4.5 — POST /api/admin/devices/{id}/config/advanced */
export async function configAdvanced(deviceId, body, { signal, confirm } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/config/advanced`,
    { method: "POST", auth: true,
      body: { ...body, ...criticalConfirmFields(confirm) }, signal },
  );
}

/** GET /api/admin/devices/{id}/config/recent?limit=20 */
export async function listDeviceConfigRecent(deviceId, { limit = 20, signal } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/config/recent?limit=${limit}`,
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 4.4.2 — GET /api/admin/devices/{id}/config
 *  Retorna snapshot atual do device + state (synced/pending/outdated/unknown). */
export async function getDeviceConfigSnapshot(deviceId, { signal } = {}) {
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/config`,
    { method: "GET", auth: true, signal },
  );
}

// ============================================================================
// Sprint 4.6 — Config Templates
// ============================================================================

/** GET /api/admin/config-templates */
export async function listConfigTemplates({ scope, enabledOnly, signal } = {}) {
  const qs = new URLSearchParams();
  if (scope) qs.set("scope", scope);
  if (enabledOnly) qs.set("enabled_only", "true");
  const q = qs.toString();
  return request(`/api/admin/config-templates${q ? "?" + q : ""}`, {
    method: "GET", auth: true, signal,
  });
}

/** POST /api/admin/config-templates */
export async function createConfigTemplate(body, { signal } = {}) {
  return request("/api/admin/config-templates", {
    method: "POST", auth: true, body, signal,
  });
}

/** GET /api/admin/config-templates/{id} */
export async function getConfigTemplate(id, { signal } = {}) {
  return request(`/api/admin/config-templates/${encodeURIComponent(id)}`, {
    method: "GET", auth: true, signal,
  });
}

/** PUT /api/admin/config-templates/{id} */
export async function updateConfigTemplate(id, body, { signal } = {}) {
  return request(`/api/admin/config-templates/${encodeURIComponent(id)}`, {
    method: "PUT", auth: true, body, signal,
  });
}

/** DELETE /api/admin/config-templates/{id} */
export async function deleteConfigTemplate(id, { signal } = {}) {
  return request(`/api/admin/config-templates/${encodeURIComponent(id)}`, {
    method: "DELETE", auth: true, signal,
  });
}

/** POST /api/admin/config-templates/{id}/apply */
export async function applyConfigTemplate(id, body, { signal, confirm } = {}) {
  return request(`/api/admin/config-templates/${encodeURIComponent(id)}/apply`, {
    method: "POST", auth: true,
    body: { ...body, ...criticalConfirmFields(confirm) }, signal,
  });
}

/** GET /api/admin/config-templates/runs */
export async function listConfigTemplateRuns({ templateId, limit, signal } = {}) {
  const qs = new URLSearchParams();
  if (templateId) qs.set("template_id", templateId);
  if (limit) qs.set("limit", String(limit));
  const q = qs.toString();
  return request(`/api/admin/config-templates/runs${q ? "?" + q : ""}`, {
    method: "GET", auth: true, signal,
  });
}

/** GET /api/admin/config-templates/runs/{id} */
export async function getConfigTemplateRun(runId, { signal } = {}) {
  return request(`/api/admin/config-templates/runs/${encodeURIComponent(runId)}`, {
    method: "GET", auth: true, signal,
  });
}

/**
 * Sprint 4.6.7 — POST /api/admin/config-templates/runs/{id}/cancel
 *
 * Cancela comandos pending/delivered de um run. NÃO toca commands acked.
 * Retorna CancelRunOut com canceled_commands, already_acked, skipped_terminal.
 *
 * Erros possíveis:
 *   404 — run não existe
 *   409 — run é dry_run (não há commands reais a cancelar)
 */
export async function cancelConfigTemplateRun(runId, { signal } = {}) {
  return request(`/api/admin/config-templates/runs/${encodeURIComponent(runId)}/cancel`, {
    method: "POST", auth: true, signal,
  });
}

/**
 * Sprint 4.9 — GET /api/admin/config-templates/{id}/diff?device_ids=...
 *
 * Compara payload do template com o snapshot atual de cada device.
 * Retorna TemplateDiffOut com sections, changes coloridas
 * (new/changed/risky), risky_changes consolidado e warnings.
 *
 * Limite: 100 devices por chamada.
 *
 * Erros:
 *   400 — device_ids vazio ou > 100
 *   404 — template não existe
 */
export async function diffConfigTemplate(templateId, deviceIds = [], { signal } = {}) {
  if (!deviceIds.length) throw new ApiError("deviceIds não pode ser vazio");
  const params = new URLSearchParams();
  for (const did of deviceIds) params.append("device_ids", did);
  return request(
    `/api/admin/config-templates/${encodeURIComponent(templateId)}/diff?${params.toString()}`,
    { method: "GET", auth: true, signal },
  );
}

/**
 * Sprint 4.9.1 — POST /api/admin/config-templates/{id}/diff/bulk
 *
 * Diff de uma seleção resolvida via ApplyTargetSelector (customer_id,
 * group_id, region_id, tag_ids, device_ids) + filtros (online_only,
 * relay_state, min_fw_version). Retorna BulkDiffOut com risk_summary
 * agregado.
 *
 * Limite: 500 devices por chamada (limite de segurança — para mais,
 * particione com filtros adicionais).
 *
 * Erros:
 *   404 — template não existe
 *   413 — selector resolveu > 500 devices
 *   422 — selector vazio (Pydantic)
 */
export async function diffConfigTemplateBulk(templateId, selector, { signal } = {}) {
  return request(
    `/api/admin/config-templates/${encodeURIComponent(templateId)}/diff/bulk`,
    {
      method: "POST", auth: true, signal,
      body: selector,
    },
  );
}

/**
 * Sprint 4.9.2 — POST /api/admin/config-templates/{id}/apply-filtered
 *
 * Aplica template em subset baseado em diff. Body inclui ApplyTargetSelector
 * + filtros opcionais:
 *   include_only_changed: bool
 *   include_only_risky: bool
 *   exclude_without_snapshot: bool
 *   exclude_identical: bool
 *   selected_device_ids: string[]   ← manual override (INTERSECT)
 * + os mesmos params do apply normal (dry_run, stagger_seconds,
 *   command_stagger_seconds, force, confirm_risky).
 *
 * Erros:
 *   404 — template não existe
 *   409 — template desabilitado OU confirm_risky_required
 *   422 — selector vazio
 */
export async function applyFilteredConfigTemplate(templateId, payload, { signal, confirm } = {}) {
  return request(
    `/api/admin/config-templates/${encodeURIComponent(templateId)}/apply-filtered`,
    {
      method: "POST", auth: true, signal,
      body: { ...payload, ...criticalConfirmFields(confirm) },
    },
  );
}

/**
 * Enfileira um único comando simples (ping/reboot/relay_on/relay_off).
 * Para relay_reboot use enqueueRelayReboot (já existente acima).
 */
export async function enqueueSimpleCommand(deviceId, type, { signal, confirm } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const encoded = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${encoded}/commands`, {
    method: "POST",
    auth: true,
    body: { type, payload: {}, ...criticalConfirmFields(confirm) },
    signal,
  });
}

/**
 * Sprint 4.7 — GET /api/admin/devices/{id}/schedule-events
 *
 * Lista histórico de execuções da agenda do device. Ordem desc por
 * executed_at. Paginação via limit (1-200, default 50) + offset.
 *
 * Erros:
 *   404 — device não existe
 */
export async function listScheduleEvents(deviceId, { limit = 50, offset = 0, signal } = {}) {
  if (!deviceId) throw new ApiError("deviceId ausente");
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (offset > 0) params.set("offset", String(offset));
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/schedule-events?${params.toString()}`,
    { method: "GET", auth: true, signal },
  );
}

// ============================================================================
// Sprint 5.0 — Provisionamento QR + IXC
// ============================================================================

/** Sprint 5.0 — GET /api/admin/ixc/test (botão TESTAR IXC) */
export async function ixcTest({ signal } = {}) {
  return request("/api/admin/ixc/test", { method: "GET", auth: true, signal });
}

/** Sprint 5.0 — GET /api/admin/ixc/customers/search?q=&limit= */
export async function ixcSearchCustomers(q, { limit = 20, signal } = {}) {
  const params = new URLSearchParams();
  params.set("q", q);
  params.set("limit", String(limit));
  return request(
    `/api/admin/ixc/customers/search?${params.toString()}`,
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 5.0 — GET /api/admin/ixc/customer/{id}/contracts */
export async function ixcCustomerContracts(customerId, { signal } = {}) {
  if (!customerId) throw new ApiError("customer_id ausente");
  return request(
    `/api/admin/ixc/customer/${encodeURIComponent(customerId)}/contracts`,
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 5.0 — GET /api/admin/ixc/customer/{id}/logins */
export async function ixcCustomerLogins(customerId, { signal } = {}) {
  if (!customerId) throw new ApiError("customer_id ausente");
  return request(
    `/api/admin/ixc/customer/${encodeURIComponent(customerId)}/logins`,
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 5.0 — GET /api/admin/ixc/customer/{id}/summary (cliente + contratos + logins) */
export async function ixcCustomerSummary(customerId, { signal } = {}) {
  if (!customerId) throw new ApiError("customer_id ausente");
  return request(
    `/api/admin/ixc/customer/${encodeURIComponent(customerId)}/summary`,
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 5.0 — POST /api/admin/devices/bind (cria/atualiza binding QR → IXC) */
export async function createDeviceBinding(payload, { signal } = {}) {
  return request("/api/admin/devices/bind", {
    method: "POST",
    auth: true,
    body: payload,
    signal,
  });
}

/** PROVISIONING-STOCK-QR-6 — POST /api/admin/devices/bindings/{qr_serial}/replace
 *  Troca segura de Sonoff: arquiva o device antigo + cria binding pendente p/ o
 *  novo QR. NÃO envia comando ao equipamento. Erros: 409 (QR já vinculado a outro
 *  cliente), 400 (QR igual/sem confirm), 422 (QR não-numérico), 404 (binding). */
export async function replaceDeviceBinding(qrSerial, { new_qr_serial, reason, confirm } = {}, { signal } = {}) {
  return request(
    `/api/admin/devices/bindings/${encodeURIComponent(qrSerial)}/replace`,
    { method: "POST", auth: true, body: { new_qr_serial, reason, confirm }, signal },
  );
}

/** Sprint 5.0 — GET /api/admin/devices/bindings?limit=&offset=&customer_id=&search=
 *  Hotfix S5.0.1 — adicionado parâmetro `search` (LIKE em qr_serial,
 *  device_id, customer_id, pppoe_login).
 */
export async function listDeviceBindings({ limit = 50, offset = 0, customerId, search, signal } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (offset > 0) params.set("offset", String(offset));
  if (customerId) params.set("customer_id", customerId);
  if (search && search.trim()) params.set("search", search.trim());
  return request(
    `/api/admin/devices/bindings?${params.toString()}`,
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 5.2A — GET /api/admin/devices/bindings/pending
 *  Lista bindings com status 'pending_device' (aba Pendentes). */
export async function listPendingBindings({ signal } = {}) {
  return request(
    "/api/admin/devices/bindings/pending",
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 5.0 — GET /api/admin/devices/bindings/{qr_serial} */
export async function getDeviceBinding(qrSerial, { signal } = {}) {
  if (!qrSerial) throw new ApiError("qr_serial ausente");
  return request(
    `/api/admin/devices/bindings/${encodeURIComponent(qrSerial)}`,
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 5.0 — DELETE /api/admin/devices/bindings/{qr_serial} */
export async function deleteDeviceBinding(qrSerial, { signal } = {}) {
  if (!qrSerial) throw new ApiError("qr_serial ausente");
  return request(
    `/api/admin/devices/bindings/${encodeURIComponent(qrSerial)}`,
    { method: "DELETE", auth: true, signal },
  );
}

// ============================================================================
// Sprint 5.0.3 — DeviceDetail e DevicesList enriquecidos com vínculo IXC
// ============================================================================

/** Sprint 5.0.3 — GET /api/admin/devices/{device_id}/ixc-binding
 *  Retorna o vínculo IXC ativo do device (404 se não houver). */
export async function getDeviceIxcBinding(deviceId, { signal } = {}) {
  if (!deviceId) throw new ApiError("deviceId ausente");
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/ixc-binding`,
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 5.0.3 — GET /api/admin/devices/with-ixc-binding
 *  Lista device_ids que têm binding 'bound' (para pintar badge na lista). */
export async function listDevicesWithIxcBinding({ signal } = {}) {
  return request(
    "/api/admin/devices/with-ixc-binding",
    { method: "GET", auth: true, signal },
  );
}

// ============================================================================
// Sprint 5.0.6 — Templates iniciais aplicados pós-bind
// ============================================================================

/** Sprint 5.0.6 — GET /api/admin/ixc/initial-templates
 *  Lista templates iniciais (Residencial, Empresarial, Personalizado). */
export async function listInitialTemplates({ signal } = {}) {
  return request(
    "/api/admin/ixc/initial-templates",
    { method: "GET", auth: true, signal },
  );
}

/** Sprint 5.0.6 — GET /api/admin/ixc/initial-templates/{id}/preview
 *  Preview dos comandos que o template enfileiraria. */
export async function previewInitialTemplate(templateId, customerId, { signal } = {}) {
  if (!templateId) throw new ApiError("templateId ausente");
  const params = new URLSearchParams();
  if (customerId) params.set("customer_id", customerId);
  return request(
    `/api/admin/ixc/initial-templates/${encodeURIComponent(templateId)}/preview?${params.toString()}`,
    { method: "GET", auth: true, signal },
  );
}

// ============================================================================
// Sprint 5.2B — Portal do Técnico / Instalação em Campo
// ============================================================================

/**
 * Hotfix CORS — installerFetch: cliente HTTP do Portal do Técnico.
 *
 * O Portal do Técnico (/installer) roda no celular do técnico. As
 * funções abaixo NÃO passam pelo request()/buildUrl, que prefixa o
 * apiUrl ABSOLUTO configurado para o NOC (ex: http://192.168.95.2:8000).
 * Uma URL absoluta cross-origin dispara o preflight CORS do browser,
 * que o backend rejeita (400) — quebrava salvar QR/cliente/finish.
 *
 * installerFetch usa SEMPRE caminho RELATIVO `/api/installer${path}`:
 *   - same-origin → o browser não faz preflight CORS;
 *   - em dev, o proxy do Vite (/api → 127.0.0.1:8000) encaminha;
 *   - em produção, o nginx que serve o frontend faz o mesmo proxy.
 *
 * Nunca usa apiUrl, localhost:8000 nem porta absoluta. O Portal do
 * Técnico é público (sem admin token).
 */
async function installerFetch(path, { method = "GET", body, signal } = {}) {
  const url = `/api/installer${path}`;   // SEMPRE relativo
  // SAAS-INSTALLER-FRONTEND-BEARER-1 — envia o token de SESSÃO Bearer quando há
  // sessão de usuário no navegador (técnico/NOC logado); NUNCA X-Admin-Token,
  // NUNCA recovery. As rotas de sessão do installer aceitam Bearer OPCIONAL
  // (backend BOOTSTRAP-1): com Bearer de tenant a sessão herda/checa o tenant.
  // Em 401 numa rota PÚBLICA de sessão (Bearer stale/expirado), refaz tokenless
  // (fallback capability-token via session_id), preservando o fluxo público.
  // /ixc/* (IXC fechado/platform-only) NUNCA cai para tokenless.
  const isIxc = path.startsWith("/ixc/");
  const sessionToken = loadSession()?.token || null;

  const doFetch = (withBearer) => {
    const headers = { Accept: "application/json" };
    if (withBearer && sessionToken) {
      headers["Authorization"] = `Bearer ${sessionToken}`;
    }
    const init = { method, headers, signal };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    return fetch(url, init);
  };

  let res;
  try {
    res = await doFetch(!!sessionToken);
    if (res.status === 401 && sessionToken && !isIxc) {
      // Bearer inválido/expirado numa rota pública de sessão → repete sem auth.
      res = await doFetch(false);
    }
  } catch (err) {
    if (err.name === "AbortError") throw err;
    throw new ApiError(`Falha de rede: ${err.message}`, { status: 0 });
  }

  let resBody = null;
  const ctype = res.headers.get("content-type") || "";
  if (ctype.includes("application/json")) {
    try {
      resBody = await res.json();
    } catch {
      resBody = null;
    }
  }
  if (!res.ok) {
    const detail =
      (resBody && (resBody.detail || resBody.message)) ||
      `HTTP ${res.status}`;
    throw new ApiError(
      typeof detail === "string" ? detail : JSON.stringify(detail),
      { status: res.status },
    );
  }
  return resBody;
}

/** Sprint 5.2B — POST /api/installer/sessions — cria sessão (draft) */
export async function createInstallationSession({ installer, qrSerial, signal } = {}) {
  return installerFetch("/sessions", {
    method: "POST", signal,
    body: { installer: installer || null, qr_serial: qrSerial || null },
  });
}

/** Hotfix 5.2B-A — GET /api/installer/templates
 *  Templates iniciais para o Portal do Técnico — SEM admin token. */
export async function listInstallerTemplates({ signal } = {}) {
  return installerFetch("/templates", { method: "GET", signal });
}

/** Sprint 5.2B — GET /api/installer/sessions/{id} */
export async function getInstallationSession(sessionId, { signal } = {}) {
  if (!sessionId) throw new ApiError("session_id ausente");
  return installerFetch(
    `/sessions/${encodeURIComponent(sessionId)}`,
    { method: "GET", signal },
  );
}

/** Hotfix 5.2B-C — GET /api/installer/sessions/{id}/device-status
 *  Status do equipamento vinculado: online, último HB, fw. */
export async function getInstallerDeviceStatus(sessionId, { signal } = {}) {
  if (!sessionId) throw new ApiError("session_id ausente");
  return installerFetch(
    `/sessions/${encodeURIComponent(sessionId)}/device-status`,
    { method: "GET", signal },
  );
}

/** Sprint 5.2B — PATCH /api/installer/sessions/{id} — avança o wizard */
export async function updateInstallationSession(sessionId, fields, { signal } = {}) {
  if (!sessionId) throw new ApiError("session_id ausente");
  return installerFetch(
    `/sessions/${encodeURIComponent(sessionId)}`,
    { method: "PATCH", signal, body: fields },
  );
}

/** Sprint 5.2B — POST /api/installer/sessions/{id}/finish */
export async function finishInstallationSession(sessionId, { signal } = {}) {
  if (!sessionId) throw new ApiError("session_id ausente");
  return installerFetch(
    `/sessions/${encodeURIComponent(sessionId)}/finish`,
    { method: "POST", signal },
  );
}

/** Sprint 5.2B — POST /api/installer/sessions/{id}/cancel */
export async function cancelInstallationSession(sessionId, { signal } = {}) {
  if (!sessionId) throw new ApiError("session_id ausente");
  return installerFetch(
    `/sessions/${encodeURIComponent(sessionId)}/cancel`,
    { method: "POST", signal },
  );
}

/** Hotfix CORS — GET /api/installer/ixc/customers/search
 *  Busca de cliente IXC para o Portal do Técnico — relativa, sem token. */
export async function searchInstallerCustomers(q, { limit = 20, signal } = {}) {
  const params = new URLSearchParams();
  params.set("q", q);
  params.set("limit", String(limit));
  return installerFetch(
    `/ixc/customers/search?${params.toString()}`,
    { method: "GET", signal },
  );
}

/** Hotfix CORS — GET /api/installer/ixc/customer/{id}/summary
 *  Resumo do cliente (contratos + logins) — relativa, sem token. */
export async function getInstallerCustomerSummary(customerId, { signal } = {}) {
  if (!customerId) throw new ApiError("customer_id ausente");
  return installerFetch(
    `/ixc/customer/${encodeURIComponent(customerId)}/summary`,
    { method: "GET", signal },
  );
}

/** Sprint 5.2B + NOC-INSTALLATIONS-CLEANUP — GET /api/admin/installations.
 *  Default esconde rascunhos abandonados; includeDrafts=true mostra todos.
 *  Resposta inclui { sessions, count, total, hidden_stale_drafts }. */
export async function listInstallations({ status, includeDrafts = false, signal } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (includeDrafts) params.set("include_drafts", "true");
  const qs = params.toString();
  return request(
    `/api/admin/installations${qs ? "?" + qs : ""}`,
    { method: "GET", auth: true, signal },
  );
}

// ============================================================================
// Sprint 5.2D — Estoque + Reserva + Logística
// ============================================================================

/** S5.2D — GET /api/admin/inventory — lista + dashboard */
export async function listInventory(filters = {}, { signal } = {}) {
  const params = new URLSearchParams();
  for (const k of [
    "status", "qr_serial", "device_id", "customer_id",
    "contract_id", "installer", "team",
  ]) {
    if (filters[k]) params.set(k, filters[k]);
  }
  if (filters.offline === true) params.set("offline", "true");
  if (filters.offline === false) params.set("offline", "false");
  const qs = params.toString();
  return request(
    `/api/admin/inventory${qs ? "?" + qs : ""}`,
    { method: "GET", auth: true, signal },
  );
}

/** S5.2D — POST /api/admin/inventory — cria item de estoque */
export async function createInventoryItem(body, { signal } = {}) {
  return request("/api/admin/inventory", {
    method: "POST", auth: true, signal, body,
  });
}

/** S5.2D — PATCH /api/admin/inventory/{id} */
export async function updateInventoryItem(id, fields, { signal } = {}) {
  if (!id) throw new ApiError("id ausente");
  return request(
    `/api/admin/inventory/${encodeURIComponent(id)}`,
    { method: "PATCH", auth: true, signal, body: fields },
  );
}

/** S5.2D — POST /api/admin/inventory/reserve */
export async function reserveInventoryItem(body, { signal } = {}) {
  return request("/api/admin/inventory/reserve", {
    method: "POST", auth: true, signal, body,
  });
}

/** S5.2D — POST /api/admin/inventory/install */
export async function installInventoryItem(body, { signal } = {}) {
  return request("/api/admin/inventory/install", {
    method: "POST", auth: true, signal, body,
  });
}

// ============================================================================
// Sprint 5.3 — Saúde da Frota (Fleet Health)
// ============================================================================

/** S5.3 — GET /api/admin/health — lista (filtro opcional) */
export async function listHealth(filter, { signal } = {}) {
  const qs = filter ? `?filter=${encodeURIComponent(filter)}` : "";
  return request(`/api/admin/health${qs}`, {
    method: "GET", auth: true, signal,
  });
}

/** S5.3 — GET /api/admin/health/summary — cards do dashboard */
export async function healthSummary({ signal } = {}) {
  return request("/api/admin/health/summary", {
    method: "GET", auth: true, signal,
  });
}

/** S5.3 — GET /api/admin/health/{device_id} */
export async function healthDevice(deviceId, { signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  return request(
    `/api/admin/health/${encodeURIComponent(deviceId)}`,
    { method: "GET", auth: true, signal },
  );
}

/** S7.8 — GET /api/admin/health/rankings — top-N por critério */
export async function healthRankings({ limit, signal } = {}) {
  const qs = limit ? `?limit=${encodeURIComponent(limit)}` : "";
  return request(`/api/admin/health/rankings${qs}`, {
    method: "GET", auth: true, signal,
  });
}

/** S7.8.3 — GET /api/admin/health/{device_id}/explain — por que este score */
export async function healthExplain(deviceId, { signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  return request(
    `/api/admin/health/${encodeURIComponent(deviceId)}/explain`,
    { method: "GET", auth: true, signal },
  );
}

// ============================================================================
// Sprint 5.5 — Equipe Técnica + Ordens de Serviço (Field Ops)
// ============================================================================

/** S5.5 — GET /api/admin/teams */
export async function listTeams({ signal } = {}) {
  return request("/api/admin/teams", {
    method: "GET", auth: true, signal,
  });
}

/** S5.5 — POST /api/admin/teams */
export async function createTeam(body, { signal } = {}) {
  return request("/api/admin/teams", {
    method: "POST", auth: true, signal, body,
  });
}

/** S5.5 — PATCH /api/admin/teams/{id} */
export async function updateTeam(id, fields, { signal } = {}) {
  if (!id) throw new ApiError("id ausente");
  return request(
    `/api/admin/teams/${encodeURIComponent(id)}`,
    { method: "PATCH", auth: true, signal, body: fields },
  );
}

/** S5.5 — GET /api/admin/workorders (filtros opcionais) */
export async function listWorkOrders(filters = {}, { signal } = {}) {
  const params = new URLSearchParams();
  for (const k of [
    "status", "team_id", "customer_id", "qr_serial", "device_id",
  ]) {
    if (filters[k]) params.set(k, filters[k]);
  }
  const qs = params.toString();
  return request(
    `/api/admin/workorders${qs ? "?" + qs : ""}`,
    { method: "GET", auth: true, signal },
  );
}

/** S5.5 — POST /api/admin/workorders */
export async function createWorkOrder(body, { signal } = {}) {
  return request("/api/admin/workorders", {
    method: "POST", auth: true, signal, body,
  });
}

/** S5.5 — PATCH /api/admin/workorders/{id} */
export async function updateWorkOrder(id, fields, { signal } = {}) {
  if (!id) throw new ApiError("id ausente");
  return request(
    `/api/admin/workorders/${encodeURIComponent(id)}`,
    { method: "PATCH", auth: true, signal, body: fields },
  );
}

// ============================================================================
// Sprint 5.6 — Templates Automáticos por Perfil
// ============================================================================

/** S5.6 — GET /api/admin/template-rules — lista + dashboard */
export async function listTemplateRules({ signal } = {}) {
  return request("/api/admin/template-rules", {
    method: "GET", auth: true, signal,
  });
}

/** S5.6 — POST /api/admin/template-rules */
export async function createTemplateRule(body, { signal } = {}) {
  return request("/api/admin/template-rules", {
    method: "POST", auth: true, signal, body,
  });
}

/** S5.6 — PATCH /api/admin/template-rules/{id} (inclui ativar/desativar) */
export async function updateTemplateRule(id, fields, { signal } = {}) {
  if (!id) throw new ApiError("id ausente");
  return request(
    `/api/admin/template-rules/${encodeURIComponent(id)}`,
    { method: "PATCH", auth: true, signal, body: fields },
  );
}

/** S5.6 — DELETE /api/admin/template-rules/{id} */
export async function deleteTemplateRule(id, { signal } = {}) {
  if (!id) throw new ApiError("id ausente");
  return request(
    `/api/admin/template-rules/${encodeURIComponent(id)}`,
    { method: "DELETE", auth: true, signal },
  );
}

/** S5.6 — POST /api/admin/template-rules/suggest — sugere template */
export async function suggestTemplate(ctx, { signal } = {}) {
  return request("/api/admin/template-rules/suggest", {
    method: "POST", auth: true, signal, body: ctx,
  });
}

// ============================================================================
// Sprint 5.7 — Logs + Auditoria + Timeline
// ============================================================================

/** S5.7 — GET /api/admin/audit — lista + dashboard */
export async function listAudit(filters = {}, { signal } = {}) {
  const params = new URLSearchParams();
  for (const k of [
    "entity_type", "action", "actor", "device_id", "customer_id",
    "limit",
  ]) {
    if (filters[k]) params.set(k, filters[k]);
  }
  const qs = params.toString();
  return request(
    `/api/admin/audit${qs ? "?" + qs : ""}`,
    { method: "GET", auth: true, signal },
  );
}

/** S5.7 — GET /api/admin/audit/{entity_type}/{entity_id} */
export async function listAuditForEntity(
  entityType, entityId, { signal } = {},
) {
  if (!entityType || !entityId) throw new ApiError("entidade ausente");
  return request(
    `/api/admin/audit/${encodeURIComponent(entityType)}`
      + `/${encodeURIComponent(entityId)}`,
    { method: "GET", auth: true, signal },
  );
}

/**
 * GET /api/admin/devices/{device_id}/timeline
 * S7.7 Fase 1 — timeline consolidada (audit/schedule/command/ota) com
 * filtros (kinds csv, severity, since) e paginação por cursor (before/limit).
 * Sem args = comportamento compatível com o uso anterior.
 */
export async function deviceTimeline(
  deviceId,
  { kinds, severity, since, before, limit, signal } = {},
) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const p = new URLSearchParams();
  if (kinds) p.set("kinds", kinds);
  if (severity) p.set("severity", severity);
  if (since) p.set("since", since);
  if (before) p.set("before", before);
  if (limit) p.set("limit", String(limit));
  const qs = p.toString();
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/timeline${qs ? "?" + qs : ""}`,
    { method: "GET", auth: true, signal },
  );
}

/**
 * S5.7 — POST /api/admin/audit/export — baixa CSV/JSON.
 * Retorna um Blob (o caller cria o link de download).
 */
export async function exportAudit(body, { signal } = {}) {
  // SAAS-AUTH-RETIRE-ADMIN-TOKEN — usa authHeaders() (Bearer de super_admin OU
  // ADMIN_TOKEN de bancada), como o resto da API. Antes exigia ADMIN_TOKEN e
  // rejeitava usuário logado por Bearer; agora o super_admin logado exporta sem
  // configurar ADMIN_TOKEN. Fetch direto (não-JSON: retorna Blob) reusando
  // authHeaders() para a credencial.
  const res = await fetch(buildUrl("/api/admin/audit/export"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(body || {}),
    signal,
  });
  if (!res.ok) {
    throw new ApiError(`export falhou (${res.status})`, { status: res.status });
  }
  return res.blob();
}

// ============================================================================
// Sprint 5.8 — Observabilidade
// ============================================================================

/** S5.8 — GET /api/admin/observability/summary — cards do NOC */
export async function observabilitySummary({ signal } = {}) {
  return request("/api/admin/observability/summary", {
    method: "GET", auth: true, signal,
  });
}

// ============================================================================
// Sprint 5.9 — Alertas + Notificações
// ============================================================================

/** S5.9 — GET /api/admin/alerts — lista + dashboard */
export async function listAlerts(filters = {}, { signal } = {}) {
  const params = new URLSearchParams();
  for (const k of [
    "status", "severity", "entity_type", "device_id", "since_hours", "limit",
  ]) {
    if (filters[k]) params.set(k, filters[k]);
  }
  const qs = params.toString();
  return request(
    `/api/admin/alerts${qs ? "?" + qs : ""}`,
    { method: "GET", auth: true, signal },
  );
}

/** S5.9 — GET /api/admin/alerts/count — badge global */
export async function alertCount({ signal } = {}) {
  return request("/api/admin/alerts/count", {
    method: "GET", auth: true, signal,
  });
}

/** S5.9 — POST /api/admin/alerts/ack — reconhece (ack) ou fecha */
export async function ackAlert(alertId, action = "ack", ackBy, { signal } = {}) {
  return request("/api/admin/alerts/ack", {
    method: "POST", auth: true, signal,
    body: { alert_id: alertId, action, ack_by: ackBy },
  });
}

/** S5.9 — POST /api/admin/alerts/test — dispara alerta de teste */
export async function testAlert(body, { signal } = {}) {
  return request("/api/admin/alerts/test", {
    method: "POST", auth: true, signal, body,
  });
}

/** S5.9 — GET /api/admin/alerts/notifiers — estado dos canais */
export async function listNotifiers({ signal } = {}) {
  return request("/api/admin/alerts/notifiers", {
    method: "GET", auth: true, signal,
  });
}

/** S5.9 — POST /api/admin/alerts/scan — roda as varreduras */
export async function scanAlerts({ signal } = {}) {
  return request("/api/admin/alerts/scan", {
    method: "POST", auth: true, signal,
  });
}

// ============================================================================
// Sprint 6.0 — Automação / Playbooks IF-THEN
// ============================================================================

/** S6.0 — GET /api/admin/automation/rules — lista + dashboard */
export async function listAutomationRules({ signal } = {}) {
  return request("/api/admin/automation/rules", {
    method: "GET", auth: true, signal,
  });
}

/** S6.0 — POST /api/admin/automation/rules */
export async function createAutomationRule(body, { signal } = {}) {
  return request("/api/admin/automation/rules", {
    method: "POST", auth: true, signal, body,
  });
}

/** S6.0 — PATCH /api/admin/automation/rules/{id} */
export async function updateAutomationRule(id, fields, { signal } = {}) {
  if (!id) throw new ApiError("id ausente");
  return request(
    `/api/admin/automation/rules/${encodeURIComponent(id)}`,
    { method: "PATCH", auth: true, signal, body: fields },
  );
}

/** S6.0 — POST /api/admin/automation/rules/{id}/run — executa agora */
export async function runAutomationRule(id, force = false, { signal } = {}) {
  if (!id) throw new ApiError("id ausente");
  const qs = force ? "?force=true" : "";
  return request(
    `/api/admin/automation/rules/${encodeURIComponent(id)}/run${qs}`,
    { method: "POST", auth: true, signal },
  );
}

/** S6.0 — POST /api/admin/automation/scan — avalia todas as regras */
export async function scanAutomation({ signal } = {}) {
  return request("/api/admin/automation/scan", {
    method: "POST", auth: true, signal,
  });
}

/** S6.0 — GET /api/admin/automation/runs — histórico de execuções */
export async function listAutomationRuns(filters = {}, { signal } = {}) {
  const params = new URLSearchParams();
  for (const k of ["rule_id", "status", "limit"]) {
    if (filters[k]) params.set(k, filters[k]);
  }
  const qs = params.toString();
  return request(
    `/api/admin/automation/runs${qs ? "?" + qs : ""}`,
    { method: "GET", auth: true, signal },
  );
}



// ── Sprint 6.4 — Fleet OTA Manager ───────────────────────────────────

export async function getOtaOverview({ signal } = {}) {
  return request("/api/admin/ota/overview", { auth: true, signal });
}

export async function listOtaRollouts(
  { activeOnly = false, signal } = {}
) {
  const qs = activeOnly ? "?active_only=true" : "";
  return request(`/api/admin/ota/rollouts${qs}`, {
    auth: true, signal,
  });
}

export async function getOtaRollout(batchId, { signal } = {}) {
  return request(
    `/api/admin/ota/rollouts/${encodeURIComponent(batchId)}`,
    { auth: true, signal }
  );
}

export async function getOtaRolloutDevices(
  batchId, { status, signal } = {}
) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return request(
    `/api/admin/ota/rollouts/${encodeURIComponent(batchId)}/devices${qs}`,
    { auth: true, signal }
  );
}

export async function getOtaRolloutCommands(batchId, { signal } = {}) {
  return request(
    `/api/admin/ota/rollouts/${encodeURIComponent(batchId)}/commands`,
    { auth: true, signal }
  );
}

export async function getOtaOutdated(targetVersion, { signal } = {}) {
  return request(
    `/api/admin/ota/outdated?target_version=${encodeURIComponent(targetVersion)}`,
    { auth: true, signal }
  );
}

export async function createOtaRollout(payload, { signal, confirm } = {}) {
  return request("/api/admin/ota/rollouts", {
    auth: true, method: "POST",
    body: { ...payload, ...criticalConfirmFields(confirm) }, signal,
  });
}

export async function otaRolloutAction(
  batchId, action, { query, signal, confirm } = {}
) {
  const qs = query ? "?" + query : "";
  return request(
    `/api/admin/ota/rollouts/${encodeURIComponent(batchId)}/${action}${qs}`,
    { auth: true, method: "POST", body: criticalConfirmFields(confirm), signal }
  );
}

// ====== Sprint 6.4A: Device Center ======

/**
 * GET /api/admin/devices/{id}/center — payload agregado do
 * Device Center (resumo, saúde, estoque, cliente, OS, alertas,
 * automação, comandos, timeline, firmware/rollouts).
 */
export async function getDeviceCenter(deviceId, { signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/center`,
    { auth: true, signal }
  );
}

/**
 * Enfileira um comando simples (sem payload) para o device.
 * type ∈ { ping, reboot, relay_on, relay_off }.
 */
export async function enqueueDeviceCommand(deviceId, type, { signal, confirm } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const allowed = ["ping", "reboot", "relay_on", "relay_off"];
  if (!allowed.includes(type)) {
    throw new ApiError("tipo de comando inválido: " + type);
  }
  return request(
    `/api/admin/devices/${encodeURIComponent(deviceId)}/commands`,
    { auth: true, method: "POST", body: { type, ...criticalConfirmFields(confirm) }, signal }
  );
}

// ====== Sprint 6.5: Multi-tenant ======

/** GET /api/admin/tenants — lista empresas. */
export async function listTenants({ signal } = {}) {
  return request("/api/admin/tenants", { auth: true, signal });
}

/** POST /api/admin/tenants — cria empresa. */
export async function createTenant(payload, { signal } = {}) {
  return request("/api/admin/tenants", {
    auth: true, method: "POST", body: payload, signal,
  });
}

/** PATCH /api/admin/tenants/{id} — atualiza white-label. */
export async function updateTenant(tenantId, payload, { signal } = {}) {
  return request(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}`,
    { auth: true, method: "PATCH", body: payload, signal }
  );
}

/** GET /api/admin/users — lista usuários (opcional: por tenant). */
export async function listTenantUsers(tenantId, { signal } = {}) {
  const qs = tenantId
    ? "?tenant_id=" + encodeURIComponent(tenantId) : "";
  return request("/api/admin/users" + qs, { auth: true, signal });
}

/** POST /api/admin/users — vincula usuário a um tenant. */
export async function addTenantUser(payload, { signal } = {}) {
  return request("/api/admin/users", {
    auth: true, method: "POST", body: payload, signal,
  });
}

/** PATCH /api/admin/users/{id}/role — altera o papel. */
export async function updateUserRole(userPk, role, { signal } = {}) {
  return request(
    `/api/admin/users/${encodeURIComponent(userPk)}/role`,
    { auth: true, method: "PATCH", body: { role }, signal }
  );
}

/** GET /api/admin/whoami — contexto RBAC atual. */
export async function getWhoAmI({ signal } = {}) {
  return request("/api/admin/whoami", { auth: true, signal });
}

// ====== Sprint 6.5F: Schedule cloud (agenda/automação) ======

/** GET /devices/{id}/schedule — lista a agenda do device. */
export async function listSchedule(deviceId, { signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const d = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${d}/schedule`, {
    auth: true, signal,
  });
}

/** POST /devices/{id}/schedule — cria uma entrada de agenda. */
export async function createSchedule(deviceId, payload,
  { signal, confirm } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const d = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${d}/schedule`, {
    auth: true, method: "POST",
    body: { ...payload, ...criticalConfirmFields(confirm) }, signal,
  });
}

/** PATCH /devices/{id}/schedule/{sid} — atualiza uma entrada. */
export async function updateSchedule(deviceId, scheduleId,
  payload, { signal, confirm } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const d = encodeURIComponent(deviceId);
  const s = encodeURIComponent(scheduleId);
  return request(`/api/admin/devices/${d}/schedule/${s}`, {
    auth: true, method: "PATCH",
    body: { ...payload, ...criticalConfirmFields(confirm) }, signal,
  });
}

/** DELETE /devices/{id}/schedule/{sid} — remove uma entrada. */
export async function deleteSchedule(deviceId, scheduleId,
  { signal, confirm } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const d = encodeURIComponent(deviceId);
  const s = encodeURIComponent(scheduleId);
  return request(`/api/admin/devices/${d}/schedule/${s}`, {
    auth: true, method: "DELETE",
    body: criticalConfirmFields(confirm), signal,
  });
}

/** POST /devices/{id}/schedule/sync — marca a agenda como sincronizada. */
export async function syncSchedule(deviceId, { signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const d = encodeURIComponent(deviceId);
  return request(`/api/admin/devices/${d}/schedule/sync`, {
    auth: true, method: "POST", signal,
  });
}

/** GET /devices/{id}/schedule/executions — histórico de execuções. */
export async function listScheduleExecutions(deviceId,
  { limit = 50, signal } = {}) {
  if (!deviceId) throw new ApiError("device_id ausente");
  const d = encodeURIComponent(deviceId);
  const qs = `?limit=${encodeURIComponent(limit)}`;
  return request(
    `/api/admin/devices/${d}/schedule/executions${qs}`,
    { auth: true, signal }
  );
}

export { ApiError };
